import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useCallback, useState } from 'react';
import { useSnackbar } from 'ui-components';
import { sendAuthReminder } from '~/clientApi/sendAuthReminder';
import {
  QUERY_STATUS,
  SNACKBAR_ERROR_GENERIC_OPTIONS,
  SOCIAL_PLATFORMS,
  SOCIAL_PLATFORMS_REMINDER_DAYS,
} from '~/constants';
import { getPlatformStatus } from '~/core/utils';
import { CREATORS_COLLECTION_QUERY_KEY } from '~/queries/keys';
import { ICreator } from '~/types';
import { formatDate } from '~/utils';
import { closest } from '~/utils-ts';

export interface IUsePlatformReminderOptions {
  creator: ICreator;
  minDays?: number;
}

export const usePlatformReminder = ({
  creator,
  minDays = SOCIAL_PLATFORMS_REMINDER_DAYS,
}: IUsePlatformReminderOptions) => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<QUERY_STATUS>(QUERY_STATUS.idle);
  const { expired } = getPlatformStatus(creator);
  const snackbar = useSnackbar();

  const expiredToRemind = expired.reduce(
    (acc: SOCIAL_PLATFORMS[], platform) => {
      const data = creator.platforms[platform];
      if (data?.reminderSentAt) {
        const dateISO = dayjs(data.reminderSentAt).toISOString();
        const reminderSentAt = new Date(dateISO);
        const days = dayjs().diff(reminderSentAt, 'days', true);
        if (days >= minDays) {
          acc.push(platform);
        }
        return acc;
      }
      acc.push(platform);
      return acc;
    },
    [],
  );

  const handleReminder = useCallback(async () => {
    if (expiredToRemind.length) {
      setStatus(QUERY_STATUS.loading);
      try {
        await sendAuthReminder(creator.id, expiredToRemind);
        await queryClient.invalidateQueries({
          queryKey: [CREATORS_COLLECTION_QUERY_KEY],
        });
        snackbar.success({
          description: 'Your Reminder has been successfully sent',
        });
        setStatus(QUERY_STATUS.idle);
      } catch {
        setStatus(QUERY_STATUS.error);
        snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creator.id, expiredToRemind, snackbar]);

  const latestReminderSent = closest(
    dayjs().unix(),
    Object.entries(creator?.platforms).reduce(
      (acc: number[], [_platform, { reminderSentAt }]) => {
        if (reminderSentAt) {
          acc.push(dayjs(reminderSentAt).unix());
        }
        return acc;
      },
      [],
    ),
  );

  const latestReminderSentAt =
    latestReminderSent &&
    formatDate(dayjs.unix(latestReminderSent).toISOString());

  return {
    expired,
    expiredToRemind,
    handleReminder,
    latestReminderSent:
      latestReminderSent && dayjs.unix(latestReminderSent).toISOString(),
    latestReminderSentAt,
    showReminder: !!expiredToRemind.length,
    status,
  };
};
